'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Region } from '@/components/region';
import { useTakeover } from '../takeover-provider';

export const TakeoverReplacementHost = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation('takeover');
    const location = useLocation();
    const { replacement } = useTakeover();
    const containerRef = useRef<HTMLDivElement>(null);
    const previousReplacement = useRef(replacement);
    const previousLocationKey = useRef(location.key);
    const [announcement, setAnnouncement] = useState<{ id: number; message: string } | null>(null);

    useEffect(() => {
        if (previousLocationKey.current !== location.key) {
            previousLocationKey.current = location.key;
            previousReplacement.current = replacement;
            setAnnouncement(null);
            return;
        }

        if (replacement === previousReplacement.current) return;

        previousReplacement.current = replacement;
        setAnnouncement((current) => ({
            id: (current?.id ?? 0) + 1,
            message: replacement ? t('announcements.loaded') : t('announcements.restored'),
        }));
        const frame = requestAnimationFrame(() => containerRef.current?.focus());
        return () => cancelAnimationFrame(frame);
    }, [location.key, replacement, t]);

    return (
        <>
            <div
                ref={containerRef}
                tabIndex={-1}
                role={replacement ? 'region' : undefined}
                aria-label={replacement ? t('contentRegionLabel') : undefined}
                className="min-w-0 outline-none">
                {replacement ? (
                    <Region
                        page={replacement.page}
                        regionId={replacement.regionId}
                        errorElement={children}
                        fallbackOnEmpty
                    />
                ) : (
                    children
                )}
            </div>
            <span key={announcement?.id} role="status" aria-live="polite" className="sr-only">
                {announcement?.message}
            </span>
        </>
    );
};
